import Services, { handleError } from "./index.service";
import { getSession } from "@/hooks/useSession";
import Config from "@config";
const { OshimedBackend, OshimedAPI } = Services;
import State, { STATE, InvitationState } from "@state";
import { sha256 } from "@/utils/crypto.js";
import AuthService from "@services/auth.service.js";

/**
 * Sends invitation data to the backend for creation.
 * @param {object} payload - Invitation data (email, firstname, lastname, invitation_type).
 * @returns {Promise<object>} The API response or a standardized error object.
 */
/** ------------------ cmt 451294 ------------------
const sendInvitation = async (payload) => {
  try {
    const session = getSession();
    const { apiToken } = session || {};

    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "invitation-no-api-token",
        errorKey: 100001,
      };
    }

    const response = await OshimedBackend.post("/invitation/", payload, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Frontend Service error in sendInvitation:", error);
    return handleError(error, 100002);
  }
};
*-------------------------------------------------*/

const getInvitationDetails = async (
  invitationId,
  authId,
  status = Config.INVITATION_STATUSES
) => {
  console.log('=== status === invitation.service.js === key: 833795 ===');
  console.dir(status, { depth: null, colors: true })
  console.log('=================================');
  try {
    const response = await OshimedBackend.get(
      `/invitation/${invitationId}/${authId}`,
      {
        params: {
          status: status.toString(),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Frontend Service error in getInvitationDetails:", error);
    return handleError(error, 100012);
  }
};

/**
 * Sends invitation data to the backend for creation (public/unauthenticated version).
 * @param {object} payload - Invitation data (email, firstname, lastname, invitation_type).
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const sendPublicInvitation = async (payload) => {
  try {
    // No authorization header required for the public endpoint
    const response = await OshimedBackend.post(
      "invitation/public/invitation/send",
      payload
    );
    return response.data;
  } catch (error) {
    console.error(
      "Frontend Service error in sendPublicInvitation (unauthenticated):",
      error
    );
    return handleError(error, 100017);
  }
};
// TODO: A supprimer une fois que sendPublicInvitation sera protegé
const sendProtectedInvitation = async (payload) => {
  try {
    // No authorization header required for the public endpoint
    const response = await OshimedBackend.post(
      "invitation/public/invitation/send-protected",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Frontend Service error in sendProtectedInvitation:", error);
    return handleError(error, 100017);
  }
};

/**
 * Retrieves invitations sent by the authenticated user.
 * @returns {Promise<object>} An array of invitation objects or a standardized error object.
 */
const getSentInvitationsBySender = async () => {
  try {
    const session = getSession();
    const { apiToken } = session || {};

    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "sent-invitations-no-api-token",
        errorKey: 100021,
      };
    }

    const response = await OshimedBackend.get("/invitation/sent", {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Frontend Service error in getSentInvitations:", error);
    return handleError(error, 100022);
  }
};

/**
 * Marks the invitation as “joined” when l’utente invitato accede.
 * @param {string} invitationId
 */
const joinInvitation = async (invitationId) => {
  try {
    const session = getSession();
    const { apiToken } = session || {};
    if (!apiToken) {
      return {
        success: false,
        message: "Not authenticated.",
        errorCode: "no-token",
        errorKey: 100040,
      };
    }
    const response = await OshimedBackend.post(
      `/invitation/${invitationId}/join`,
      {},
      { headers: { Authorization: `Bearer ${apiToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Service error in joinInvitation:", error);
    return handleError(error, 100041);
  }
};

/**
 * Marks the invitation as “active” when il manager conferma.
 * @param {string} invitationId
 */
const confirmInvitation = async (invitationId) => {
  try {
    const session = getSession();
    const { platformToken } = session || {};
    if (!platformToken) {
      return {
        success: false,
        message: "Not authenticated.",
        errorCode: "no-token",
        errorKey: 100050,
      };
    }

    const response = await OshimedBackend.post(
      `/invitation/${invitationId}/confirm`,
      {}
    );

    return response.data;
  } catch (error) {
    console.error("Service error in confirmInvitation:", error);
    return handleError(error, 100051);
  }
};

/**
 * Sends patient invitation data to the backend.
 * @param {object} payload - Patient invitation data (email, firstname, lastname, phone_number, patient_address_street, patient_address_number, patient_address_country, patient_address_city, patient_address_postal_code, study_id, investigator_id).
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const sendPatient = async (payload) => {
  try {
    const session = getSession();
    const { apiToken } = session || {};
    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "patient-invitation-no-api-token",
        errorKey: 100060,
      };
    }
    const response = await OshimedBackend.post("/invitation/patient", payload, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Frontend Service error in sendPatient:", error);
    return handleError(error, 100061);
  }
};

/**
 * Accepts an investigator invitation.
 * @param {string} invitationId - The ID of the invitation to accept.
 * @returns {Promise<object>} API response or a standardized error object.
 */
const acceptInvestigatorInvitation = async (
  invitationId,
  studyId,
  siteId,
  entityId,
  entityDidAddress,
  delegate
) => {
  try {
    const session = getSession();
    const platformToken = session?.platformToken;
    const apiToken = session?.apiToken;

    if (!platformToken) {
      return {
        success: false,
        message: "Missing platformToken in session. User not authenticated.",
        errorCode: "accept-invitation-no-api-token",
        errorKey: 100065,
      };
    }

    console.log("=== delegate === invitation.service.js === key: 109865 ===");
    console.dir(delegate, { depth: null, colors: true });
    console.log("=================================");

    console.log("=== STATE === invitation.service.js === key: 995136 ===");
    console.dir(STATE, { depth: null, colors: true });
    console.log("=================================");

    //return true;
    const hashedEmail = await sha256(session.user.email);
    const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);

    let bcData;

    if (tmpKeyRes?.success === false) {
      console.error(
        "V0_DEBUG: Missing xApiKey in session for acceptInvestigatorInvitation."
      );
      return {
        success: false,
        message: "Missing xApiKey in session for acceptInvestigatorInvitation.",
        errorCode: "accept-investigator-invitation-no-x-api-key",
        errorKey: 198885,
      };
    }
    const xApiKey = tmpKeyRes;

    try {
      const response = await OshimedAPI.patch(
        "/study/investigator-delegate",
        { studyId, siteId, entityId, entityDidAddress, delegate },
        {
          headers: {
            "x-api-key": xApiKey,
            Authorization: `Bearer ${session.apiToken}`,
          },
        }
      );

      if (!response.data) {
        return {
          success: false,
          message: "Error accepting investigator invitation.",
          fromError: response,
          errorCode: "accept-investigator-invitation-error",
          errorKey: 738786,
        };
      }

      console.log('=== response.data === invitation.service.js === key: 388716 ===');
      console.dir(response.data, { depth: null, colors: true })
      console.log('=================================');

      bcData = response.data;

    } catch (error) {
      console.log("=== error === invitation.service.js === key: 149728 ===");
      console.dir(error, { depth: null, colors: true });
      console.log("=================================");
      return handleError(error, 746894);
    }

    const response = await OshimedBackend.post(
      "/invitation/investigator/accept-invitation",
      { invitationId, delegate, bcData },
      {
        headers: {
          Authorization: `Bearer ${platformToken}`,
        },
      }
    );

    console.log(
      "=== response.data === invitation.service.js === key: 511772 ==="
    );
    console.dir(response.data, { depth: null, colors: true });
    console.log("=================================");

    return response.data;
  } catch (error) {
    console.error(
      "Frontend Service error in acceptInvestigatorInvitationBackend:",
      error
    );
    return handleError(error, 100066);
  }
};

/**
 * Declines an investigator invitation.
 * @param {string} invitationId - The ID of the invitation to decline.
 * @returns {Promise<object>} API response or a standardized error object.
 */
const declineInvestigatorInvitation = async (invitationId) => {
  try {
    const session = getSession();
    const { apiToken } = session || {};
    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "decline-invitation-no-api-token",
        errorKey: 100071,
      };
    }
    const response = await OshimedBackend.post(
      "/invitation/investigator/decline-invitation",
      { invitationId },
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Frontend Service error in declineInvestigatorInvitation:",
      error
    );
    return handleError(error, 100072);
  }
};

/**
 * Cancels an invitation (logical delete).
 * @param {string} invitationId - The ID of the invitation to cancel.
 * @returns {Promise<object>} API response or a standardized error object.
 */
const cancelInvitation = async (invitationId) => {
  try {
    const session = getSession();
    const { apiToken } = session || {};

    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "cancel-invitation-no-api-token",
        errorKey: 100080,
      };
    }

    const response = await OshimedBackend.post(
      `/invitation/${invitationId}/cancel`,
      {},
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Frontend Service error in cancelInvitation:", error);
    return handleError(error, 100081);
  }
};

/**
 * Accepte une invitation "participant" (utilisateur déjà enregistré).
 * @param {string} invitationId - ID de l'invitation à accepter.
 * @param {string} participantId - UUID du participant (généré côté controller FE).
 * @returns {Promise<object>} Réponse API standardisée.
 */
const acceptParticipantInvitation = async (invitationId, userId, participantId) => {
  try {
    const session = getSession();
    const apiToken = session?.apiToken;

    if (!apiToken) {
      return {
        success: false,
        message: "Missing API token in session. User not authenticated.",
        errorCode: "accept-participant-invitation-no-api-token",
        errorKey: 100090,
      };
    }

    const response = await OshimedBackend.post(
      "/invitation/participant/accept-invitation",
      { invitationId, userId, participantId },
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Frontend Service error in acceptParticipantInvitation:", error);
    return handleError(error, 100091);
  }
};

export default {
  //sendInvitation,
  getInvitationDetails,
  sendPublicInvitation,
  getSentInvitationsBySender,
  sendProtectedInvitation,
  confirmInvitation,
  joinInvitation,
  sendPatient,
  acceptInvestigatorInvitation,
  declineInvestigatorInvitation,
  cancelInvitation,
  acceptParticipantInvitation,
};
